// src/pages/LinkedInCallback.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { setAuthToken, authFetch } from '../utils/authFetch';
import './LinkedInCallback.css';

const LinkedInCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleLinkedInCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkedInCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        throw new Error(`LinkedIn OAuth error: ${errorParam}`);
      }

      if (!code) {
        throw new Error('No authorization code received from LinkedIn');
      }

      const storedState = sessionStorage.getItem('linkedin_oauth_state');
      
      if (storedState === null) {
        if (localStorage.getItem('isLoggedIn') === 'true') {
          navigate('/tool');
          return;
        }
        navigate('/signup');
        return;
      }

      if (state !== storedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }

      sessionStorage.removeItem('linkedin_oauth_state');

      const redirectUri = `${window.location.origin}/linkedin-callback`;
      
      // linkedin_auth is a public endpoint — plain fetch is correct here
      const response = await fetch(`${API_ENDPOINTS.REGISTER_USER.replace('/register_user', '/linkedin_auth')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.body || errorData.message || 'LinkedIn authentication failed');
      }

      const userData = await response.json();

      // ✅ Store the backend-issued JWT for all subsequent API calls
      if (userData.token) {
        setAuthToken(userData.token);
      }

      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userEmail', userData.email);
      localStorage.setItem('userFirstName', userData.first_name || '');
      localStorage.setItem('userLastName', userData.last_name || '');
      localStorage.setItem('loginSource', 'linkedin');
      
      if (userData.picture) {
        localStorage.setItem('linkedinPhotoURL', userData.picture);
        localStorage.setItem('googlePhotoURL', userData.picture);
      }

      if (!userData.existing_user) {
        await registerUser(userData);
      }

      const pendingReferralCode = sessionStorage.getItem('pending_referral_code');
      if (pendingReferralCode && !userData.existing_user) {
        await applyReferralCode(userData.email, pendingReferralCode);
        sessionStorage.removeItem('pending_referral_code');
      }

      const redirectPath = localStorage.getItem('redirectAfterLogin') || '/tool';
      localStorage.removeItem('redirectAfterLogin');
      
      navigate(redirectPath);

    } catch (error) {
      setError(error.message);
      setLoading(false);
      setTimeout(() => { navigate('/signup'); }, 3000);
    }
  };

  // register_user is a public endpoint — plain fetch is correct
  const registerUser = async (userData) => {
    try {
      const response = await fetch(API_ENDPOINTS.REGISTER_USER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userData.email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email,
          phone: 'N/A',
          login_source: 'linkedin'
        })
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        if (responseText.toLowerCase().includes('already exists') || 
            responseText.toLowerCase().includes('duplicate') ||
            response.status === 409) {
          return;
        }
        throw new Error('Failed to register user');
      }
    } catch (error) {
      // Don't block login if registration fails
    }
  };

  // apply_referral is a protected endpoint — use authFetch
  const applyReferralCode = async (email, referralCode) => {
    try {
      await authFetch(API_ENDPOINTS.APPLY_REFERRAL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: email,
          referral_code: referralCode
        })
      });
    } catch (error) {
      // Silently fail - don't block login
    }
  };

  if (loading) {
    return (
      <div className="linkedin-callback-container">
        <div className="callback-card">
          <div className="spinner-large"></div>
          <h2>Signing you in with LinkedIn...</h2>
          <p>Please wait while we complete your authentication</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="linkedin-callback-container">
        <div className="callback-card error">
          <div className="error-icon">❌</div>
          <h2>Authentication Failed</h2>
          <p className="error-message">{error}</p>
          <p className="redirect-message">Redirecting you back to sign up...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default LinkedInCallback;